import { useAppSelector } from "@/store/hooks"
import { getUserCreds } from "@/utils/utils"

/**
 * "Somebody wants to speed the game up", thrown across the middle of the board
 * for a couple of seconds when a fast-pace vote opens.
 *
 * The vote itself is answered in the panel at the head of the left player
 * column (FastPaceVotePanel), which then carries it for the full 45 seconds.
 * This exists only for the opening moment: the vote has to be unanimous and a
 * player who never answers counts as a no, so a proposal that goes unnoticed by
 * somebody staring at the board is a proposal that quietly fails. It says where
 * to answer and gets out of the way.
 *
 * Deliberately smaller than the other center notifications, and click-through:
 * turns carry on while a vote is open, so this lands on somebody who may be
 * mid-roll, and it must not swallow the button underneath it.
 *
 * The `mobile:` overrides are the phone card — that variant is a max-width of
 * 999.98px, the same width that flips `isMobile`. Two things change together
 * there: the card is capped so it stops taking most of the width of the screen,
 * and every size is respelled in px, because index.css halves the root
 * font-size under 1000px. The rem sizes above land at half scale on exactly the
 * screens with the least room to spare, so the narrower card ends up carrying
 * larger type than the wide one it replaces.
 */
export const FastPaceVoteStartedNotification: React.FC<
  FastPaceVoteStartedNotificationProps
> = ({ playerOrder }) => {
  const players = useAppSelector(
    (state) => state.global.myGame?.situation?.players
  )
  const users = useAppSelector((state) => state.global.myGame?.players)

  const proposer = players?.find((player) => player.order === playerOrder)
  const isMe = proposer?.id === getUserCreds().email
  const proposerName = isMe
    ? "You"
    : users?.find((user) => user.email === proposer?.id)?.username || "A player"

  return (
    <div
      data-testid="notification.fast-pace-vote-started"
      className="toast-pop pointer-events-none fixed left-1/2 top-1/2 z-20 flex -translate-x-1/2 -translate-y-1/2 items-center gap-5 rounded-2xl border-2 border-amber-300/60 bg-[rgba(20,14,4,0.92)] px-8 py-5 font-poppins text-white shadow-[0_20px_50px_rgba(0,0,0,0.65)] backdrop-blur-sm mobile:max-w-[230px] mobile:gap-3 mobile:rounded-xl mobile:px-4 mobile:py-3"
    >
      <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-amber-400/20 ring-2 ring-amber-300/50 mobile:h-[30px] mobile:w-[30px] mobile:rounded-lg">
        <i className="fa-solid fa-bolt text-3xl text-amber-300 mobile:text-[17px]" />
      </span>
      <div>
        <div className="text-sm font-bold uppercase tracking-[0.15em] text-amber-400 mobile:text-[11px] mobile:tracking-[0.12em]">
          Fast pace vote
        </div>
        <div className="mt-1 text-3xl font-bold leading-tight mobile:text-[22px]">
          <span className="text-amber-200">{proposerName}</span>
          <span className="text-white">
            {" "}
            want{isMe ? "" : "s"} to speed the game up
          </span>
        </div>
        <div className="mt-1.5 flex items-center gap-2 text-base text-white/60 mobile:mt-1 mobile:gap-1.5 mobile:text-[12px]">
          <i className="fa-solid fa-arrow-up-long -rotate-45 text-white/40" />
          Answer in the top-left corner — everyone has to agree
        </div>
      </div>
    </div>
  )
}
